"use client";

import React, { useState } from "react";
import { Calendar } from "@/shadcn-components/ui/calendar";
import { Button } from "@/shadcn-components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn-components/ui/select";
import { ChevronLeft, ChevronRight, X, Calendar as CalendarIcon } from "lucide-react";
import { addDays, format, isSameDay, isWithinInterval } from "date-fns";

interface DatePickerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentMonth: Date;
  onMonthChange: (newMonth: Date) => void;
  selectedStartDate: string;
  selectedEndDate: string;
  datePickerType: "start" | "end";
  onDateSelect: (dateStr: string) => void;
  onTypeChange: (type: "start" | "end") => void;
}

export default function DatePickerDrawer({
  isOpen,
  onClose,
  currentMonth,
  onMonthChange,
  selectedStartDate,
  selectedEndDate,
  datePickerType,
  onDateSelect,
  onTypeChange,
}: DatePickerDrawerProps) {
  const [showMonthYearSelector, setShowMonthYearSelector] = useState(false);
  // Helper do konwersji Date na string DD.MM
  const dateToString = (date: Date): string => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    return `${day}.${month}`;
  };

  // Helper do konwersji string DD.MM na Date
  const stringToDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    const [day, month] = dateStr.split(".");
    if (!day || !month) return null;
    const year = new Date().getFullYear();
    return new Date(year, parseInt(month) - 1, parseInt(day));
  };

  const startDate = stringToDate(selectedStartDate);
  const endDate = stringToDate(selectedEndDate);
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;
    
    // Sprawdź czy data nie jest w przyszłości
    if (date > today) return;
    
    // Sprawdź czy dla end date nie jest przed start date
    if (datePickerType === "end" && startDate && date < startDate) return;
    
    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
    
    onDateSelect(dateToString(date));
    
    // NIE zamykamy drawer automatycznie - użytkownik może wybrać więcej dat
  };

  const isDateDisabled = (date: Date) => {
    if (date > today) return true;
    if (datePickerType === "end" && startDate && date < startDate) return true;
    return false;
  };

  const isDateSelected = (date: Date): boolean => {
    return Boolean(
      (startDate && isSameDay(date, startDate)) ||
        (endDate && isSameDay(date, endDate))
    );
  };

  const handlePrevMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    onMonthChange(newMonth);
  };

  const handleNextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    if (newMonth > today) return;
    onMonthChange(newMonth);
  };

  const isNextDisabled = () => {
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    return nextMonth > today;
  };

  // Generate months and years for selectors
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const handleMonthSelect = (monthIndex: string) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(parseInt(monthIndex));
    
    // Check if the new month is not in the future
    if (newMonth <= today) {
      onMonthChange(newMonth);
    }
    setShowMonthYearSelector(false);
  };

  const handleYearSelect = (year: string) => {
    const newMonth = new Date(currentMonth);
    newMonth.setFullYear(parseInt(year));
    
    // Check if the new month is not in the future
    if (newMonth <= today) {
      onMonthChange(newMonth);
    }
    setShowMonthYearSelector(false);
  };

  const modifiers = {
    selected: isDateSelected,
    range_start: startDate ? (date: Date) => isSameDay(date, startDate) : undefined,
    range_end: endDate ? (date: Date) => isSameDay(date, endDate) : undefined,
    range_middle: (date: Date) => {
      if (!startDate || !endDate) return false;
      return isWithinInterval(date, { start: startDate, end: endDate }) && 
             !isSameDay(date, startDate) && 
             !isSameDay(date, endDate);
    },
  };

  const modifiersStyles = {
    range_start: {
      backgroundColor: "hsl(var(--primary))",
      color: "hsl(var(--primary-foreground))",
      borderRadius: "8px 0 0 8px",
    },
    range_end: {
      backgroundColor: "hsl(var(--primary))",
      color: "hsl(var(--primary-foreground))",
      borderRadius: "0 8px 8px 0",
    },
    range_middle: {
      backgroundColor: "hsl(var(--primary) / 0.2)",
      color: "hsl(var(--foreground))",
    },
    selected: {
      backgroundColor: "hsl(var(--primary))",
      color: "hsl(var(--primary-foreground))",
      borderRadius: "8px",
    },
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-x-0 bottom-0 z-50 bg-background border-t border-border rounded-t-xl shadow-2xl animate-in slide-in-from-bottom duration-300">
        {/* Drawer Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Select Date Range
            </h3>
            <p className="text-sm text-muted-foreground">
              Choose your start and end dates
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Date Type Selector */}
        <div className="flex p-4 gap-2">
          <Button
            variant={datePickerType === "start" ? "default" : "outline"}
            onClick={() => onTypeChange("start")}
            className="flex-1 transition-all duration-200"
          >
            Start Date
            {startDate && (
              <span className="ml-2 text-xs opacity-80">
                {format(startDate, "MMM d")}
              </span>
            )}
          </Button>
          <Button
            variant={datePickerType === "end" ? "default" : "outline"}
            onClick={() => onTypeChange("end")}
            className="flex-1 transition-all duration-200"
            disabled={!startDate}
          >
            End Date
            {endDate && (
              <span className="ml-2 text-xs opacity-80">
                {format(endDate, "MMM d")}
              </span>
            )}
          </Button>
        </div>

        {/* Calendar Navigation */}
        <div className="flex items-center justify-between px-4 pb-4">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowMonthYearSelector(!showMonthYearSelector)}
              className="text-base font-medium text-foreground hover:bg-muted transition-colors"
            >
              <CalendarIcon className="h-4 w-4 mr-2" />
              {format(currentMonth, "MMMM yyyy")}
            </Button>
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePrevMonth}
              className="h-8 w-8 hover:bg-muted transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNextMonth}
              disabled={isNextDisabled()}
              className="h-8 w-8 hover:bg-muted transition-colors disabled:opacity-50"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Month/Year Selector */}
        {showMonthYearSelector && (
          <div className="px-4 pb-4 border-b border-border">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Month
                </label>
                <Select 
                  value={currentMonth.getMonth().toString()} 
                  onValueChange={handleMonthSelect}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((month, index) => {
                      const testMonth = new Date(currentMonth.getFullYear(), index, 1);
                      const isDisabled = testMonth > today;
                      
                      return (
                        <SelectItem 
                          key={index} 
                          value={index.toString()}
                          disabled={isDisabled}
                        >
                          {month}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Year
                </label>
                <Select 
                  value={currentMonth.getFullYear().toString()} 
                  onValueChange={handleYearSelect}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Calendar */}
        <div className="px-2 pb-6 max-h-80 overflow-y-auto">
          <Calendar
            mode="single"
            selected={datePickerType === "start" ? startDate || undefined : endDate || undefined}
            onSelect={handleDateSelect}
            disabled={isDateDisabled}
            month={currentMonth}
            onMonthChange={onMonthChange}
            modifiers={modifiers}
            modifiersStyles={modifiersStyles}
            className="rounded-md border-0 p-0 w-full"
            classNames={{
              months: "flex flex-col w-full",
              month: "space-y-4 w-full",
              caption: "hidden",
              caption_label: "text-sm font-medium",
              nav: "hidden",
              table: "w-full border-collapse",
              head_row: "flex w-full mb-2",
              head_cell: "text-muted-foreground rounded-md font-medium text-sm flex-1 text-center py-2 min-w-0",
              row: "flex w-full mb-1",
              cell: "relative p-1 text-center text-sm focus-within:relative focus-within:z-20 flex-1 min-w-0",
              day: "h-12 w-full min-w-12 p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 text-sm",
              day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground shadow-md font-medium",
              day_today: "bg-accent text-accent-foreground font-semibold ring-2 ring-primary/50",
              day_outside: "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
              day_disabled: "text-muted-foreground opacity-30 cursor-not-allowed hover:bg-transparent hover:scale-100",
              day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
              day_hidden: "invisible",
            }}
          />
        </div>

        {/* Current Selection Display */}
        <div className="px-4 pb-4 border-t border-border pt-4 bg-muted/30">
          <div className="flex justify-between items-center text-sm">
            <div>
              <span className="text-muted-foreground">Start: </span>
              <span className="font-medium text-foreground">
                {startDate ? format(startDate, "MMM d, yyyy") : "Not selected"}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">End: </span>
              <span className="font-medium text-foreground">
                {endDate ? format(endDate, "MMM d, yyyy") : "Not selected"}
              </span>
            </div>
          </div>
        </div>

        {/* Done Button */}
        <div className="p-4 border-t border-border">
          <Button
            onClick={onClose}
            className="w-full"
            disabled={!startDate || !endDate}
          >
            Done
          </Button>
        </div>
      </div>
    </>
  );
}